import { DisplayIf_Operator } from '@policyfly/protobuf/pfschema'
import { preferNumberCompare } from '@policyfly/utils/number'
import { getOneofKindValue, revertPrimitiveValue } from '@policyfly/utils/protobuf'
import { castArray } from '@policyfly/utils/type'

import { PayloadArray } from '@/lib/Payload'
import { devtools } from '@/plugins/devtools/api'
import { getDateToday, isValidDateString, offsetDate } from '@/utils/date'
import {
  blankInputDataResolver,
  getInputData,
  createInputDataPathResolver,
  createInputDataResolver,
} from '@/utils/inputData'

import type { PayloadLike } from '@/lib/Payload'
import type { ProtobufValueOptions } from '@/stores/protobuf'
import type {
  DisplayIf,
  DisplayIf_Condition,
  DisplayIf_Condition_Common,
  DisplayIf_Condition_Comparator,
  DisplayIf_Condition_Comparator_Array,
  DisplayIf_Condition_Comparator_Includes,
  FormComponentTagsProps,
  InputData as InputDataGrpc,
} from '@policyfly/protobuf/pfschema'
import type { FieldLabelProps } from '@policyfly/schema/types/shared/composables/fieldLabel'
import type { MatchCondition } from '@policyfly/schema/types/shared/displayIf'
import type { FormSource } from '@policyfly/schema/types/shared/formSource'
import type { InputData, InputDataResolver } from '@policyfly/schema/types/shared/inputData'
import type { PrimitiveValue as PrimitiveValueSchema, TSFixMe } from '@policyfly/types/common'
import type { FormFilter } from 'types/form'

type ComparatorFunction = (v: TSFixMe, data: InputData<FormSource>) => boolean

export interface ConditionOptions extends ProtobufValueOptions {
  payload?: PayloadLike | null
  resolver?: InputDataResolver | null
}

export interface FilterOptions extends ConditionOptions {
  label?: FieldLabelProps<FormSource>['label']
  inputData?: InputDataGrpc | null
  tags?: FormComponentTagsProps | null
}

/**
 * Checks whether a form component matches the active section filters.
 */
export function checkFilters (
  filters: FormFilter | null | undefined,
  options: FilterOptions,
): boolean {
  if (!filters) return true
  if (filters.parent && !checkFilters(filters.parent, options)) return false

  const searchQuery = filters?.searchQuery?.trim().toLocaleLowerCase()
  if (!searchQuery) return true

  const resolver = options.resolver ?? blankInputDataResolver
  const value = options.inputData
    ? getInputData(resolver(options.inputData), { ...options, resolver: null })
    : undefined

  const terms = [
    ...(typeof options.label === 'string' ? [options.label] : []),
    ...(options.tags?.tags.map((tag) => tag.value) ?? []),
    ...getSearchTerms(value),
  ]
  return terms.some((term) => term.toLocaleLowerCase().includes(searchQuery))
}

/**
 * Returns all searchable strings from a value.
 *
 * Will recursively get all string values from an object or array.
 */
function getSearchTerms (value: unknown, seen = new WeakSet<object>()): string[] {
  if (value == null) return []
  if (typeof value !== 'object') return [String(value)]

  if (seen.has(value)) return []
  seen.add(value)

  return Object.values(value).flatMap((child) => getSearchTerms(child, seen))
}

/**
 * Checks a {@link DisplayIf}.
 * Returns `true` if either no DisplayIf is passed or the DisplayIf conditions pass.
 */
export function checkDisplayIf (
  displayIf: DisplayIf | null | undefined,
  options: ConditionOptions,
): boolean {
  if (!displayIf) return true
  switch (displayIf.operator) {
    case DisplayIf_Operator.OPERATOR_OR: return displayIf.conditions.some((c) => checkCondition(c, options))
    default: return displayIf.conditions.every((c) => checkCondition(c, options))
  }
}

/**
 * Checks the provided {@link DisplayIf_Condition condition} and returns a boolean.
 */
function checkCondition (
  condition: DisplayIf_Condition,
  options: ConditionOptions,
): boolean {
  if (!condition.type.oneofKind) return true
  switch (condition.type.oneofKind) {
    case 'displayIf': return checkDisplayIf(condition.type.displayIf, options)
    case 'common': {
      const commonCondition = condition.type.common
      if (!commonCondition.inputData) return false
      const castResolver = options.resolver ?? blankInputDataResolver
      const data = castResolver(commonCondition.inputData)
      const permittedValues = commonCondition.permittedValues.length ? commonCondition.permittedValues.map((v) => revertPrimitiveValue(v)) : null
      const restrictedValues = commonCondition.restrictedValues.length ? commonCondition.restrictedValues.map((v) => revertPrimitiveValue(v)) : null
      const comparatorFn = buildComparatorFn(commonCondition.comparator, options)
      const response = getInputData<PrimitiveValueSchema>(data, { ...options, resolver: null })
      // if the permittedValues, restrictedValues & comparatorFn properties aren't set, we are only interested in whether the value exists
      if (!permittedValues && !restrictedValues && !comparatorFn) return response !== undefined
      // if restrictedValues is set, check that the value is not in that array
      if (restrictedValues && restrictedValues.includes(response)) return false
      // if permittedValues is set, check that the value is in that array
      if (permittedValues && !permittedValues.includes(response)) return false
      // if comparator is set, check against the generated function
      if (comparatorFn && !comparatorFn(response, data)) return false
      return true
    }
  }
}

const INVALID_VALUE = Symbol('invalidValue')
type TestValue = PrimitiveValueSchema | PrimitiveValueSchema[] | undefined
function getTestValue (
  comparator: DisplayIf_Condition_Comparator,
  options: ConditionOptions,
): TestValue | typeof INVALID_VALUE {
  const c = getOneofKindValue(comparator.name)
  if (!c || !('value' in c) || !c.value) return undefined
  if ('calculated' in c && c.calculated) {
    const v = (() => {
      if (c.value.value.oneofKind !== 'primitive') return null
      return revertPrimitiveValue(c.value.value.primitive)
    })()
    switch (v) {
      case 'thirtyYearsAgo':
        return new Date().getFullYear() - 30
      default:
        devtools.configWarning({
          type: 'field',
          title: 'Invalid calculated value specified',
          context: JSON.stringify(c.value),
        })
        return INVALID_VALUE
    }
  }
  if (Array.isArray(c.value)) {
    return c.value.map((v) => revertPrimitiveValue(v)!)
  }
  switch (c.value.value.oneofKind) {
    case 'inputData': return getInputData<PrimitiveValueSchema>(c.value.value.inputData, options)!
    case 'primitive': return revertPrimitiveValue(c.value.value.primitive)
    default: return INVALID_VALUE
  }
}

/**
 * Gets a list of visible indices from an array based
 * on a filter.
 */
function getFilteredArrayIndices (
  v: unknown,
  data: InputData<FormSource>,
  comparator: DisplayIf_Condition_Comparator_Array | DisplayIf_Condition_Comparator_Includes,
  options: ConditionOptions,
): number[] | null {
  if (!(v instanceof PayloadArray) && !Array.isArray(v)) return null
  if (!comparator.filter) return v.map((_, index) => index)
  const pathResolver = createInputDataPathResolver(data, options)
  return v.slice().reduce<number[]>((acc, _, index) => {
    const resolver = createInputDataResolver(pathResolver(index), options)
    if (checkDisplayIf(comparator.filter, { ...options, resolver })) {
      acc.push(index)
    }
    return acc
  }, [])
}

/**
 * Returns a comparator function that can be used against array values.
 */
function checkArrayValues (
  comparator: DisplayIf_Condition_Comparator_Includes,
  testValue: TestValue,
  options: ConditionOptions,
  valueCheckFn: (arrayValues: (PrimitiveValueSchema | undefined)[], testValues: (PrimitiveValueSchema | undefined)[]) => boolean,
): ComparatorFunction {
  const field = comparator.options!.field
  return (v, data) => {
    const indices = getFilteredArrayIndices(v, data, comparator, options)
    if (!indices) return false
    const pathResolver = createInputDataPathResolver(data, options)
    const arrayValues = indices.map((index) => {
      return getInputData<PrimitiveValueSchema>(pathResolver(`${index}.${field}`), { ...options, resolver: null })
    })
    const testValues = castArray(testValue)
    return valueCheckFn(arrayValues, testValues)
  }
}

/**
 * Builds a comparator function that can be checked against each response in a DisplayIf {@link DisplayIf_Condition_Comparator condition}.
 */
export function buildComparatorFn (
  comparator: DisplayIf_Condition_Comparator | undefined,
  options: ConditionOptions,
): null | ComparatorFunction {
  if (!comparator || typeof comparator !== 'object') return null

  const testValue = getTestValue(comparator, options)
  if (testValue === INVALID_VALUE) return null

  let fn: ComparatorFunction = (): boolean => false
  switch (comparator.name.oneofKind) {
    case 'equal': {
      fn = (v) => preferNumberCompare(v, testValue, (v1, v2) => v1 === v2)
      break
    }
    case 'notEqual': {
      fn = (v) => preferNumberCompare(v, testValue, (v1, v2) => v1 !== v2)
      break
    }
    case 'lessThan': {
      fn = (v) => preferNumberCompare(v, testValue as string | number, (v1, v2) => v1 < v2)
      break
    }
    case 'lessThanOrEqual': {
      fn = (v) => preferNumberCompare(v, testValue as string | number, (v1, v2) => v1 <= v2)
      break
    }
    case 'greaterThan': {
      fn = (v) => preferNumberCompare(v, testValue as string | number, (v1, v2) => v1 > v2)
      break
    }
    case 'greaterThanOrEqual': {
      fn = (v) => preferNumberCompare(v, testValue as string | number, (v1, v2) => v1 >= v2)
      break
    }
    case 'greaterThanOrEqualOrZero': {
      fn = (v) => preferNumberCompare(v, testValue as string | number, (v1, v2) => v1 === 0 || v1 >= v2)
      break
    }
    case 'emptyArray': {
      const c = comparator.name.emptyArray
      fn = (v, data) => {
        const indices = getFilteredArrayIndices(v, data, c, options)
        return !indices || indices.length === 0
      }
      break
    }
    case 'nonEmptyArray': {
      const c = comparator.name.nonEmptyArray
      fn = (v, data) => {
        const indices = getFilteredArrayIndices(v, data, c, options)
        return !!indices && indices.length !== 0
      }
      break
    }
    case 'includes': {
      const c = comparator.name.includes
      fn = checkArrayValues(c, testValue, options, (arrayValues, testValues) => {
        return arrayValues.some((val) => testValues.includes(val))
      })
      break
    }
    case 'includesExcept': {
      const c = comparator.name.includesExcept
      fn = checkArrayValues(c, testValue, options, (arrayValues, testValues) => {
        if (arrayValues.length === 0) return true
        return arrayValues.some((val) => !testValues.includes(val))
      })
      break
    }
    case 'notIncludes': {
      const c = comparator.name.notIncludes
      fn = checkArrayValues(c, testValue, options, (arrayValues, testValues) => {
        return arrayValues.every((val) => !testValues.includes(val))
      })
      break
    }
    case 'onlyIncludes': {
      const c = comparator.name.onlyIncludes
      fn = checkArrayValues(c, testValue, options, (arrayValues, testValues) => {
        if (arrayValues.length === 0) return false
        return arrayValues.every((val) => testValues.includes(val))
      })
      break
    }
    case 'withinDayRange': {
      const { options: o } = comparator.name.withinDayRange
      fn = (v) => {
        if (!isValidDateString(v)) return false
        const today = getDateToday()
        if (o?.start !== undefined) {
          const start = offsetDate(today, { days: o.start })
          if (start && v < start) return false
        }
        if (o?.end !== undefined) {
          const end = offsetDate(today, { days: o.end })
          if (end && v > end) return false
        }
        return true
      }
      break
    }
    case 'regex': {
      const { options: o } = comparator.name.regex
      if (!o?.regex) {
        devtools.configWarning({
          type: 'field',
          title: 'Regex comparator requires a regex to be specified',
          context: JSON.stringify(comparator),
        })
        return null
      }
      const regex = new RegExp(o.regex, o.flags)
      fn = (v) => typeof v === 'string' && regex.test(v)
      break
    }
    default: {
      devtools.configWarning({
        type: 'field',
        title: 'Invalid comparator name specified',
        description: comparator.name.oneofKind,
      })
      return null
    }
  }

  const value = getOneofKindValue(comparator.name)
  const ignoreValues = 'ignore' in value
    ? value.ignore.map((v) => revertPrimitiveValue(v))
    : null
  return (v, data) => {
    if (ignoreValues && ignoreValues.includes(v)) return true
    return fn(v, data)
  }
}

/**
 * Returns all the {@link DisplayIf_Condition_Common common conditions} in a {@link DisplayIf}.
 * Deeply recurses through the object to find all conditions.
 */
export function getAllConditions (
  displayIf: DisplayIf,
): DisplayIf_Condition_Common[] {
  return displayIf.conditions.flatMap((condition) => {
    if (condition.type.oneofKind === 'common') {
      return [condition.type.common]
    } else if (condition.type.oneofKind === 'displayIf') {
      return getAllConditions(condition.type.displayIf)
    }
    return []
  })
}

/**
 * If passed an array of conditions will return the value of the first condition that matches or the default value.
 *
 * If passed a value will return that value unless it is nullish in which case the default will be used.
 *
 * @param matcher Either an array of match conditions or a value
 * @param defaultValue The default value to use if no conditions match or if no matcher is provided
 * @param payload The local payload to use within the displayIf check
 * @param resolver The resolver to use within the displayIf check
 * @param bucket The data bucket to check
 */
export function matchCondition<T = PrimitiveValueSchema> (
  matcher: T | MatchCondition<T>[] | undefined,
  defaultValue: T,
  options: ConditionOptions,
): T {
  if (Array.isArray(matcher)) {
    for (const { value, condition } of matcher) {
      if (checkDisplayIf(condition, options)) return value
    }
    return defaultValue
  }
  return matcher ?? defaultValue
}
